import { Box, css, Grid, Grow, LinearProgress, Link, styled, Typography } from '@mui/material';
import { Fragment } from 'react';

import { ModeButton } from '@/components/routing/ModeButton';
import { useRoutes } from '@/hooks/useRoutes';
import { TriasService } from '@/modules/TriasService';
import { useRoutesStore } from '@/stores/useRoutesStore';
import { Mode } from '@/types/Mode';
import { RouteLocationType } from '@/types/RouteLocationType';

const StyledGrid = styled(Grid)(
  ({ theme }) => css`
    margin-top: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(3)};

    ${theme.breakpoints.up('md')} {
      margin-bottom: ${theme.spacing(2)};
    }
  `,
) as typeof Grid;

const StyledTypographyLoading = styled(Typography)(
  ({ theme }) => css`
    text-align: center;
    margin-top: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(1)};

    ${theme.breakpoints.up('md')} {
      margin-top: ${theme.spacing(2)};
      margin-bottom: ${theme.spacing(2)};
    }
  `,
);

const StyledLinearProgress = styled(LinearProgress)(
  ({ theme }) => css`
    margin-bottom: ${theme.spacing(3)};

    ${theme.breakpoints.up('md')} {
      margin-bottom: 0;
    }
  `,
);

const StyledTypographyInfo = styled(StyledTypographyLoading)(
  ({ theme }) => css`
    margin-bottom: ${theme.spacing(3)};
    max-width: ${theme.spacing(60)};
    margin-left: auto;
    margin-right: auto;
  `,
);

export function RoutingModeSelect() {
  const origin = useRoutesStore((state) => state.routeLocations[RouteLocationType.ORIGIN]);
  const destination = useRoutesStore((state) => state.routeLocations[RouteLocationType.DESTINATION]);
  const selectedMode = useRoutesStore((state) => state.selectedMode);

  const routes = useRoutes();
  const someRouteIsFetching = Object.values(routes).reduce(
    (previousValue, currentValue) => previousValue || !!currentValue?.isFetching,
    false,
  );

  const errors = TriasService.getErrors(selectedMode.mode && routes[selectedMode.mode].data)?.filter(
    ({ code }) => code !== 'OK',
  );

  return (
    <Fragment>
      <StyledGrid container spacing={1}>
        {[Mode.CYCLE, Mode.WALK, Mode.PUBLIC_TRANSPORT, Mode.CAR, Mode.BIKE_AND_RIDE, Mode.PARK_AND_RIDE].map(
          (mode) => (
            <ModeButton key={mode} mode={mode} />
          ),
        )}
      </StyledGrid>

      <Grow
        mountOnEnter
        unmountOnExit
        in={
          someRouteIsFetching ||
          (!someRouteIsFetching && (!selectedMode.mode || selectedMode.mode === Mode.CAR || !!errors))
        }
      >
        <Box>
          {!someRouteIsFetching && ((origin && destination && !selectedMode.mode) || selectedMode.mode === Mode.CAR) ? (
            <StyledTypographyInfo>
              In der Regel müssen zusätzlich zwischen 10&nbsp;und&nbsp;15&nbsp;Minuten Zu-&nbsp;und&nbsp;Abgangszeit bei
              der PKW-Route eingerechnet werden.
            </StyledTypographyInfo>
          ) : null}

          {errors?.map((error) => {
            let displayText;

            if (
              error.text?.text ===
              'Die errechnete Reisedistanz für das Fahrrad ist länger als das eingestellte Maximum. Bitte versuchen Sie es mit neuen Eingaben noch einmal!'
            ) {
              if (selectedMode.mode === Mode.CYCLE) {
                displayText = (
                  <Fragment>
                    Die Fahrradroute ist zu lang. Bitte versuchen Sie es mit neuen Eingaben noch einmal oder nutzen Sie
                    den{' '}
                    <Link
                      href={`https://radroutenplaner.hessen.de/map/?sharelink=1&prm=1&spt=${origin?.geometry.coordinates[0]},${origin?.geometry.coordinates[1]}&ept=${destination?.geometry.coordinates[0]},${destination?.geometry.coordinates[1]}`}
                      referrerPolicy="no-referrer"
                      sx={{ color: 'white', textDecorationColor: 'white' }}
                      target="_blank"
                    >
                      Radroutenplaner Hessen
                    </Link>{' '}
                    für längere Routen.
                  </Fragment>
                );
              } else {
                displayText = 'Die Fußroute ist zu lang. Bitte versuchen Sie es mit neuen Eingaben noch einmal!';
              }
            }

            return (
              <StyledTypographyInfo key={error.code}>
                {displayText ?? error.text?.text?.split(' HAFAS-Text')[0]}
              </StyledTypographyInfo>
            );
          })}

          {someRouteIsFetching ? (
            <Fragment>
              <StyledTypographyLoading>
                Verkehrslage, Verkehrsmeldungen und aktive Verkehrslenkungsmaßnahmen auf Ihrer Route werden ermittelt.
              </StyledTypographyLoading>
              <StyledLinearProgress variant="indeterminate" />
            </Fragment>
          ) : null}
        </Box>
      </Grow>
    </Fragment>
  );
}
